import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { identify as identifyAnalytics } from "shared/boot/analytics";
import { $streemApiAdmin, $streemApiV1 } from "shared/boot/api";
import { identify as identifyRollbar } from "shared/boot/rollbar";
import { parseDate } from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import {
  MediaRequest,
  MentionExpectation,
  Organisation,
  OrganisationReportComment,
  Role,
  TranscriptRequest,
  type User,
  UserRequest,
} from "shared/resources";
import useGlobalStore from "shared/stores/global";
import useOrganisationFeaturesStore, {
  type OrganisationFeatures,
} from "shared/stores/organisationFeatures";
import { type CurrentAdminOrganisation } from "shared/stores/user/types";
import { type Category, type Location, type Nullable } from "shared/types";
import { type FeatureKey } from "shared/types/features";

import { useUserStore } from "src/stores/user";

import {
  type GlobalContractServiceConfig,
  type GlobalMentionRequestsCount,
  type GlobalMentionReview,
  type GlobalPermission,
} from "./types";

const handleErrors = true;
const lastWeek = DateRange.lastSevenDays();

export const useGlobalAdminStore = defineStore("globalAdmin", () => {
  const globalDataLoaded = ref<boolean>(false);
  const globalDataError = ref<boolean>(false);
  const newVersion = ref<boolean>(false);

  const mentionReviews = ref<GlobalMentionReview>({
    radioCount: 0,
    tvCount: 0,
  });

  const userRequestsCount = ref<number>(0);
  const locations = ref<Location[]>([]);
  const categories = ref<Category[]>([]);
  const socialCategories = ref<Category[]>([]);
  const organisationReportComments = ref<number>(0);
  const overnightComments = ref<number>(0);
  const transcriptRequestsCount = ref<number>(0);
  const excludedMentionsCount = ref<number>(0);
  const permissions = ref<GlobalPermission[]>([]);
  const roles = ref<Role[]>([]);
  const accountManagers = ref<Pick<User, "id" | "name" | "email">[]>([]);
  const mediaRequestsCount = ref<number>(0);

  const mentionExpectationsCount = ref<GlobalMentionRequestsCount>({
    expected: 0,
    toDo: 0,
  });

  const hideHeader = ref<boolean>(false);
  const contractServices = ref<GlobalContractServiceConfig>();
  const currentOrganisation = ref<Nullable<CurrentAdminOrganisation>>(null);

  const organisationReportCommentsCounter = computed(
    () => organisationReportComments.value || 0
  );

  const overnightCommentsCounter = computed(() => overnightComments.value || 0);

  function setGlobalDataLoading() {
    globalDataLoaded.value = false;
    globalDataError.value = false;
  }

  function setGlobalDataLoaded() {
    globalDataLoaded.value = true;
    globalDataError.value = false;
  }

  async function getAppData() {
    setGlobalDataLoading();
    const userStore = useUserStore();

    await userStore.getCurrentUser();

    const { currentUser, hasOrganisationReadAccess } = storeToRefs(userStore);

    const loads = [];

    if (currentUser.value.admin) {
      loads.push(getAccountManagers());
    }

    if (hasOrganisationReadAccess.value) {
      loads.push(
        ...[
          getCategories(),
          getRoles(),
          getPermissions(),
          getContractServices(),
        ]
      );
    }

    const result = await Promise.all(loads);

    identifyRollbar({
      email: currentUser.value.email,
      id: currentUser.value.id,
      username: `${currentUser.value.first_name} ${currentUser.value.last_name}`,
    });

    identifyAnalytics(currentUser.value.id, {
      email: currentUser.value.email,
      first_name: currentUser.value.first_name,
      global_customer_id: "",
      last_name: currentUser.value.last_name,
      name: `${currentUser.value.first_name} ${currentUser.value.last_name}`,
      user_hash: currentUser.value.user_hash,
    });

    setGlobalDataLoaded();

    return result;
  }

  function getMenuCounter() {
    const userStore = useUserStore();
    const { hasNormalAccess } = storeToRefs(userStore);

    const loads = [getExcludedMentionsCount(), getUserRequestsCount()];

    if (hasNormalAccess.value) {
      const additionalLoads = [
        getTvMentionReviewsCount(),
        getRadioMentionReviewsCount(),
        getOrganisationCommentsCount(),
        getOvernightFeedbackCount(),
        getLastWeekTranscriptsCount(),
        getMediaRequestsCount(),
        getMentionExpectationsCount(),
      ];

      loads.push(...additionalLoads);
    }

    return Promise.all(loads);
  }

  function getTvMentionReviewsCount() {
    const params = {
      include_admin_only_mention_reviews: true,
      limit: 0,
      reviewed: false,
      target_type: ["TvSuper", "Caption", "TvCaption", "TvLogoAppearance"],
    };

    return $streemApiAdmin
      .get("mention_reviews", { handleErrors, params })
      .then((response) =>
        setTvMentionReviewsCount(response.headers["x-total-count"])
      );
  }

  function setTvMentionReviewsCount(count: number) {
    mentionReviews.value.tvCount = count;
  }

  function getRadioMentionReviewsCount() {
    const params = {
      include_admin_only_mention_reviews: true,
      limit: 0,
      reviewed: false,
      target_type: ["RadioClip"],
    };

    return $streemApiAdmin
      .get("mention_reviews", { handleErrors, params })
      .then((response) =>
        setRadioMentionReviewsCount(response.headers["x-total-count"])
      );
  }

  function setRadioMentionReviewsCount(count: number) {
    mentionReviews.value.radioCount = count;
  }

  function getMentionExpectationsCount() {
    const params = {
      status: ["pending", "in_progress"],
    };

    return MentionExpectation.where(params)
      .per(0)
      .stats({ total: "count", total_to_do: "count" })
      .all()
      .then((response) => {
        const toDo = response.meta.stats.total_to_do.count;
        const expected = response.meta.stats.total.count - toDo;

        setMentionExpectationsCount({ expected, toDo });
      })
      .catch(() => {
        // do nothing
      });
  }

  function setMentionExpectationsCount(count: GlobalMentionRequestsCount) {
    mentionExpectationsCount.value = count;
  }

  function getMediaRequestsCount() {
    const params = {
      archived: { match: "false" },
      createdAt: {
        gt: parseDate(lastWeek.after).toISOString(),
        lt: parseDate(lastWeek.before).toISOString(),
      },
      status: [
        "Incomplete",
        "In Progress",
        "Ready",
        "Exporting",
        "speech2text",
        "Clip ready",
        "Sent to mediatrack",
        "Error",
      ],
    };

    return MediaRequest.where(params)
      .stats({ total: "count" })
      .all()
      .then((response) =>
        setMediaRequestsCount(response.meta.stats.total.count)
      )
      .catch(() => {
        // do nothing
      });
  }

  function setMediaRequestsCount(count: number) {
    mediaRequestsCount.value = count;
  }

  function getUserRequestsCount() {
    return UserRequest.where({ status: { eq: "pending" } })
      .stats({ total: "count" })
      .all()
      .then((response) => {
        setUserRequestsCount(response.meta.stats.total.count);
      })
      .catch(() => {
        // do nothing
      });
  }

  function setUserRequestsCount(count: number) {
    userRequestsCount.value = count;
  }

  function getOrganisationFeatures(organisationId: number) {
    return Organisation.select(["id", "region", "time_zone"])
      .selectExtra(["features"])
      .find(organisationId)
      .then((response) => {
        currentOrganisation.value = {
          id: response.data.id,
          region: response.data.region,
          time_zone: response.data.timeZone,
        };

        useOrganisationFeaturesStore().resetOrganisationFeatures(
          // transform array to object format as expected by shared components for now
          (response.data.features as FeatureKey[]).reduce(
            (acc, item) => {
              acc[item] = {};

              return acc;
            },
            <OrganisationFeatures>{}
          )
        );
      })
      .catch((error) =>
        ErrorDialog("Failed to load organisation features", error)
      );
  }

  function getLocations() {
    if (locations.value.length) {
      return Promise.resolve();
    }

    return $streemApiV1
      .get<Location[]>("locations", {
        handleErrors,
        params: {
          visible: true,
        },
      })
      .then((response) => {
        setLocations(response.data);
      });
  }

  function setLocations(updatedLocations: Location[]) {
    // shared filters used in admin depend on the global store locations
    const globalStore = useGlobalStore();
    globalStore.setLocations(updatedLocations);

    locations.value = updatedLocations;
  }

  function getCategories() {
    return $streemApiV1
      .get<Category[]>("categories", {
        handleErrors,
        params: {
          include_social: true,
        },
      })
      .then((response) => {
        setCategories(response.data);
      });
  }

  function setCategories(data: Category[]) {
    // shared filters used in admin depend on the global store categories
    const globalStore = useGlobalStore();
    globalStore.setCategories(data);

    categories.value = data.filter(({ social }) => !social);
    socialCategories.value = data.filter(({ social }) => social);
  }

  function getOrganisationCommentsCount() {
    const categoryFilters = [
      "Inclusion",
      "Exclusion",
      "Client Media Update",
      "Feedback",
      "Spec Update",
      "Keyword Update",
    ];

    return OrganisationReportComment.where({
      category: categoryFilters,
      closed: false,
      parentComments: true,
    })
      .stats({ total: "count" })
      .per(0)
      .all()
      .then((response) => {
        setOrganisationReportComments(response.meta.stats.total.count);
      })
      .catch(() => {
        // do nothing
      });
  }

  function setOrganisationReportComments(count: number) {
    organisationReportComments.value = count;
  }

  function getOvernightFeedbackCount() {
    return OrganisationReportComment.where({
      category: "AM Review",
      closed: false,
      parentComments: true,
    })
      .stats({ total: "count" })
      .per(0)
      .all()
      .then((response) => {
        setOvernightFeedbackCount(response.meta.stats.total.count);
      })
      .catch(() => {
        // do nothing
      });
  }

  function setOvernightFeedbackCount(count: number) {
    overnightComments.value = count;
  }

  function getLastWeekTranscriptsCount() {
    return TranscriptRequest.where({
      archived: { match: "false" },
      createdAt: {
        gt: parseDate(lastWeek.after).toISOString(),
        lt: parseDate(lastWeek.before).toISOString(),
      },
      status: [
        "Incomplete",
        "Ready",
        "Exporting",
        "speech2text",
        "Clip ready",
        "Sent for manual transcription",
      ],
    })
      .stats({ total: "count" })
      .per(0)
      .all()
      .then((response) => {
        setTranscriptRequestsCount(response.meta.stats.total.count);
      })
      .catch(() => {
        // do nothing
      });
  }

  function setTranscriptRequestsCount(count: number) {
    transcriptRequestsCount.value = count;
  }

  function getExcludedMentionsCount() {
    return $streemApiAdmin
      .get("excluded_mentions", { handleErrors, params: { limit: 0 } })
      .then((response) => {
        setExcludedMentionsCount(response.headers["x-total-count"]);
      });
  }

  function setExcludedMentionsCount(count: number) {
    excludedMentionsCount.value = count;
  }

  function getRoles() {
    return Role.includes("permissionsRoles")
      .per(999)
      .all()
      .then((response) => {
        setRoles(response.data);
      })
      .catch((error) => ErrorDialog("Failed to roles", error));
  }

  function setRoles(updateRoles: Role[]) {
    roles.value = updateRoles;
  }

  function getPermissions() {
    return $streemApiAdmin
      .get<GlobalPermission[]>("permissions", { handleErrors })
      .then((response) => {
        setPermissions(response.data);
      });
  }

  function setPermissions(updatedPermissions: GlobalPermission[]) {
    permissions.value = updatedPermissions;
  }

  function getAccountManagers() {
    return Organisation.where({
      admin: true,
      archived: false,
      users: { active: true },
    })
      .includes({ users: ["organisation"] })
      .select({ users: ["name", "email"] })
      .all()
      .then((organisations) => {
        const users = organisations.data
          .map((organisation) => organisation.users)
          .flat();

        setAccountManagers(users);
      })
      .catch((error) => ErrorDialog("Failed to load account managers", error));
  }

  function setAccountManagers(
    updatedAccountManagers: Pick<User, "id" | "name" | "email">[]
  ) {
    accountManagers.value = updatedAccountManagers;
  }

  function toggleHideHeader() {
    hideHeader.value = !hideHeader.value;
  }

  function setHideHeader(value: boolean) {
    hideHeader.value = value;
  }

  function getContractServices() {
    return $streemApiAdmin
      .get<GlobalContractServiceConfig>(
        "organisation_contract_services/list_services"
      )
      .then((response) => {
        setContractServices(response.data);
      });
  }

  function setContractServices(
    updatedContractServices: GlobalContractServiceConfig
  ) {
    contractServices.value = updatedContractServices;
  }

  return {
    accountManagers,
    categories,
    contractServices,
    currentOrganisation,
    excludedMentionsCount,
    getAccountManagers,
    getAppData,
    getCategories,
    getContractServices,
    getExcludedMentionsCount,
    getLastWeekTranscriptsCount,
    getLocations,
    getMediaRequestsCount,
    getMentionExpectationsCount,
    getMenuCounter,
    getOrganisationCommentsCount,
    getOrganisationFeatures,
    getOvernightFeedbackCount,
    getPermissions,
    getRadioMentionReviewsCount,

    getRoles,
    getTvMentionReviewsCount,

    getUserRequestsCount,
    globalDataError,
    globalDataLoaded,
    hideHeader,
    locations,
    mediaRequestsCount,
    mentionExpectationsCount,
    mentionReviews,
    newVersion,
    organisationReportComments,
    organisationReportCommentsCounter,
    overnightComments,
    overnightCommentsCounter,
    permissions,
    roles,
    setAccountManagers,
    setCategories,
    setContractServices,
    setExcludedMentionsCount,
    setGlobalDataLoaded,
    setGlobalDataLoading,
    setHideHeader,
    setLocations,
    setMediaRequestsCount,
    setMentionExpectationsCount,
    setOrganisationReportComments,
    setOvernightFeedbackCount,
    setPermissions,
    setRadioMentionReviewsCount,
    setRoles,
    setTranscriptRequestsCount,
    setTvMentionReviewsCount,
    setUserRequestsCount,
    socialCategories,
    toggleHideHeader,
    transcriptRequestsCount,
    userRequestsCount,
  };
});

export default useGlobalAdminStore;
